import React, { useContext } from "react";
import { css } from "emotion";
import { boxStyles, colors, textSizes } from "../../styles/theme";
import GenericTextInput from "../GenericTextInput";
import Section from "../createNew/Section";
import CustomButtonBox from "../createNew/CustomButtonBox";
import BackgroundColorPicker from "../createNew/BackgroundColorPicker";
import GenericButton from "../GenericButton";
import AudioFileUpload from "../createNew/AudioFileUpload";
import TrashCan from "../TrashCan";
import Toggle from "../Toggle";
import { EditContext } from "../../context/EditSpotlightState";
import { GlobalContext } from "../../context/GlobalState";
import spotlights from "../../services/api/spotlights";
import { withRouter } from "react-router-dom";
import { useForm } from "react-hook-form";
import ErrorMessage from "../ErrorMessage";

const EditOptions = ({ history }) => {
  const {
    type,
    headline,
    setHeadline,
    ctaText,
    setCtaText,
    name,
    setName,
    linkVal,
    setLinkVal,
    primaryColor,
    secondaryColor,
    fileName,
    fileUrl,
    isLoading,
    setIsLoading,
    showCta,
    setShowCta,
  } = useContext(EditContext);
  const { currentSpotlight, setCurrentSpotlight } = useContext(GlobalContext);
  const methods = useForm();
  const { setError, errors, clearError } = methods;

  const handleUpdate = async () => {
    setIsLoading(true);
    const updateData =
      type === "text"
        ? {
            id: currentSpotlight.id,
            type: "text",
            name,
            headline,
            ctaText: showCta ? ctaText : "",
            ctaUrl: showCta ? linkVal : "",
            primaryColor,
            secondaryColor,
          }
        : {
            id: currentSpotlight.id,
            type: "audio",
            name,
            headline,
            ctaText: showCta ? ctaText : "",
            ctaUrl: showCta ? linkVal : "",
            fileUrl,
            fileName,
            primaryColor,
            secondaryColor,
          };

    // CHECKING FOR EMPTY FIELDS
    for (let prop in updateData) {
      if (
        Object.prototype.hasOwnProperty.call(updateData, prop) &&
        updateData[prop] === ""
      ) {
        const formattedProp = prop.replace(/([A-Z])/g, " $1");
        if (prop === "ctaText" || prop === "ctaUrl") {
          if (showCta) {
            setError(
              prop,
              "emptyField",
              "Add a call-to-action or toggle it off"
            );
          } else {
            clearError(prop);
          }
        } else if (prop === "fileUrl" || prop === "fileName") {
          if (type === "audio") {
            setError(
              prop,
              "emptyField",
              "Add an audio file or switch to text spotlight."
            );
          } else {
            clearError(prop);
          }
        } else {
          setError(prop, "emptyField", `A ${formattedProp} is required.`);
        }
      } else {
        clearError(prop);
      }
    }

    if (Object.keys(errors).length > 0) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const { data } = await spotlights.update(updateData);
      setCurrentSpotlight(data);
      history.push("/view");
    } catch (error) {
      if (error.response) {
        setError(
          "spotlight",
          "badCombo",
          "There was a problem updating your spotlight. Please reload and try again."
        );
      } else if (error.request) {
        setError(
          "spotlight",
          "noConnection",
          "You seem to have gotten disconnected. Make sure your internet is connected and try again."
        );
        console.log(error.request);
      } else {
        setError(
          "spotlight",
          "unexpected",
          "There was a problem creating your account. Please reload and try again."
        );
        console.log("Error", error.message);
      }
      setIsLoading(false);
    }
  };

  return (
    <div className={container}>
      {errors.headline && (
        <ErrorMessage>{errors.headline.message}</ErrorMessage>
      )}
      <Section heading="Headline">
        <GenericTextInput
          value={headline}
          onValueChange={setHeadline}
          maxLength={70}
        />
      </Section>
      <div style={{ marginBottom: "10px" }}>
        {showCta && errors.ctaText && (
          <ErrorMessage>{errors.ctaText.message}</ErrorMessage>
        )}
        {showCta && errors.ctaUrl && !errors.ctaText && (
          <ErrorMessage>{errors.ctaUrl.message}</ErrorMessage>
        )}
      </div>
      <h4>Custom Button</h4>
      <div className="toggle-container">
        <Toggle value={showCta} toggle={() => setShowCta(!showCta)} />
      </div>
      <Section>
        {showCta && (
          <CustomButtonBox
            ctaText={ctaText}
            setCtaText={setCtaText}
            linkVal={linkVal}
            setLinkVal={setLinkVal}
          />
        )}
      </Section>

      {type === "audio" && errors.fileUrl && (
        <ErrorMessage>{errors.fileUrl.message}</ErrorMessage>
      )}
      {type === "audio" && (
        <Section heading="Audio File">
          <AudioFileUpload forEdit />
        </Section>
      )}
      <Section heading="Background Color">
        <BackgroundColorPicker forEdit />
      </Section>
      {errors.name && <ErrorMessage>{errors.name.message}</ErrorMessage>}
      <Section heading="Spotlight Name">
        <GenericTextInput
          style="name-input"
          value={name}
          onValueChange={setName}
          maxLength={60}
        />
      </Section>
      <div className={errors.spotlight && "errors-container"}>
        {errors.spotlight && (
          <ErrorMessage>{errors.spotlight.message}</ErrorMessage>
        )}
      </div>
      <div className="all-buttons">
        <div className={"button-row" + (isLoading ? " disabled" : "")}>
          <TrashCan style="invisible" />
          <GenericButton
            text="save"
            style="button save"
            handleClick={handleUpdate}
          />
          <TrashCan style="trash" />
        </div>
      </div>
    </div>
  );
};

export default withRouter(EditOptions);

const container = css`
  padding: ${boxStyles.standardPadding};
  max-width: 500px;
  .all-buttons {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .button-row {
      display: flex;
      justify-content: space-between;
      margin: 10px 0;
      width: 100%;

      &.disabled {
        opacity: 0.5;
        pointer-events: none;
      }

      .button {
        width: 50%;
        p {
          padding: 5px ${boxStyles.desktopPadding};
          font-size: ${textSizes.normal};
        }
      }
      .save {
        background-color: ${colors.brandColor};
      }
      .invisible {
        display: block;
        opacity: 0;
        pointer-events: none;
      }
    }
  }

  .errors-container {
    margin-bottom: 20px;
  }

  .toggle-container {
    display: inline-block;
    vertical-align: middle;
    height: 19px;
    margin-top: -9px;
    margin-left: 16px;
  }

  h4 {
    font-size: ${textSizes.normal};
    color: ${colors.titleColor};
    letter-spacing: 1px;
    display: inline-block;
  }

  @media screen and (min-width: ${boxStyles.desktopBreak}) {
    padding: 0;
    .name-input {
      width: 50%;
    }

    h4 {
      padding-bottom: 10px;
      letter-spacing: 1px;
    }

    .all-buttons {
      .button-row {
        width: 50%;
        .save {
          width: 45%;
        }
        .invisible {
          display: none;
        }
      }
    }
  }
`;
